/**
 * Board slice (PX-W7.4, TASK-41CE1DE2) — the project's build queue: tasks by
 * status, epics as modules, the artefact under review, the run log, and the
 * six review-gate verbs.
 *
 * Nine exports are BINDING on TASK-FD4686D1 (see the batch's shared
 * interface contract §E) — `openBoard`, the seven `select*` functions and
 * `boardClosed` — plus `BoardProgress` and `HUMAN_GATED_STATUSES`, both
 * re-exported here from `../types` so a consumer can import either the
 * type surface or the slice and get the same symbols. None of the nine may
 * be renamed.
 *
 * Every verdict thunk swaps the server's RETURNED task in by `legacyId` —
 * it never re-derives the row and never refetches the list to learn the
 * outcome of its own write (the phase-gates precedent). A refusal lands in
 * `error` scoped by the task's `legacyId`; the row itself is left byte-
 * identical (INV-4.3). The client holds no queue state machine: a task's
 * rendered column is a total function of its own `status` field (INV-4.2).
 *
 * Pull to board (fix cycle 1, reverses Decision D5 — Agon 2026-09-26) follows
 * the same rule: `pullToBoard` is not optimistic; on success the server's
 * RETURNED row (now `todo`) replaces the backlog row, and the realtime
 * `task.*` frames the server publishes reload the board as for any other
 * change. A refusal lands in its OWN channel, `pullError`, scoped by the
 * task's `legacyId`, so it never shows up among the verdict refusals.
 */

import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type ActionReducerMapBuilder,
  type AsyncThunk
} from '@reduxjs/toolkit'

import { selectSessionPrincipalKey } from '@/features/auth/slices/sessionSlice'
import { selectCanWriteProject } from '@/features/projects/slices/projectDetailSlice'
import { invalidateBoard } from '@/features/realtime/slices/realtimeSlice'
import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  markServerChanged,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { boardService, type FeedbackInput } from '../services/boardService.http'
import {
  BOARD_COLUMN_OF,
  HUMAN_GATED_STATUSES,
  TASK_STATUS_VALUES,
  UNASSIGNED_EPIC_KEY,
  type ActivityRow,
  type ArtefactKindSuffix,
  type BoardColumns,
  type BoardEpicRow,
  type BoardProgress,
  type BoardTaskRow,
  type EpicFilter,
  type EpicSummary,
  type FeatureBlocked,
  type KeyedFeatureError,
  type TaskStatusValue
} from '../types'

export type { BoardProgress }
export { HUMAN_GATED_STATUSES }

/**
 * Sum-to-1 partial-credit weights, pinned against `TaskStatus.PROGRESS_WEIGHTS`.
 * Exported (deferred finding c1-5) so `boardContract.test.ts` can pin all
 * twelve numbers against the Java source instead of trusting this hand-copy
 * to stay in step silently.
 */
export const PROGRESS_WEIGHTS: Record<TaskStatusValue, number> = {
  backlog: 0,
  todo: 0,
  spec: 0.05,
  plan: 0.1,
  execute: 0.4,
  review: 0.7,
  uat: 0.9,
  test: 0.9,
  done: 1,
  blocked: 0,
  archived: 1,
  cancelled: 0
}

/** Statuses `countsTowardTotal` excludes — CANCELLED alone (`TaskStatus.java`). */
const EXCLUDED_FROM_TOTAL: TaskStatusValue = 'cancelled'

export interface BoardState extends FetchCache {
  projectId: string | null
  /** The ACTIVE board (`GET …/tasks`) — never a backlog row (INV-R.5). */
  tasks: BoardTaskRow[]
  /**
   * The BACKLOG pool (`GET …/backlog`, CR-BUILD-BOARD-EPICS-2026-09-26),
   * loaded by the same `openBoard` so both views project one load.
   */
  backlog: BoardTaskRow[]
  epics: BoardEpicRow[]
  selectedTaskId: string | null
  artefact: { kind: ArtefactKindSuffix; markdown: string } | null
  artefactLoading: boolean
  /** A 404 on the artefact read — the review card's OWN empty state, never `blocked`. */
  artefactMissing: boolean
  /**
   * A non-404 `loadArtefact` failure (deferred findings c1-4/c2-2) — its OWN
   * channel, read by `ReviewCard` alone, so the pane can show "loading" and
   * "failed" as mutually exclusive branches instead of the error alert
   * rendering underneath a loading line that never clears. Cleared by
   * `loadArtefact.pending`/`.fulfilled`, `taskSelected` and `boardClosed`.
   */
  artefactError: StatusedError | null
  activity: ActivityRow[]
  /** Project-scoped activity cache key — loaded once per project (Decision 1). */
  activityLoadedKey: string | null
  activityLoading: boolean
  /**
   * The projectId of the most recently STARTED `loadTaskActivity` request, or
   * `null` once it settles (fix cycle 1, GRP-82297E9A). Keyed by project so an
   * in-flight read for project A never vetoes a read for project B, and A's
   * late response can be told apart from B's. Set only by `.pending`, cleared
   * only by the matching `.fulfilled`/`.rejected` — `boardClosed` never
   * touches it (batch contract C-3: StrictMode runs that reducer between a
   * panel's two effect runs).
   */
  activityInFlightKey: string | null
  /**
   * A `loadTaskActivity` failure (deferred finding c2-1) — its OWN channel,
   * read by `RunLogPanel` alone, so a dead activity GET renders an in-panel
   * error with retry instead of falling through to "this task has no
   * activity". Cleared by `loadTaskActivity.pending` and `boardClosed`.
   */
  activityError: StatusedError | null
  /** The legacyId of the task whose verdict is currently in flight. */
  mutating: string | null
  /** The legacyId of the backlog task whose pull is in flight (Pull to board). */
  pulling: string | null
  /** A refused pull, scoped by the task's legacyId — rendered on that card only. */
  pullError: KeyedFeatureError | null
  /**
   * scope = the taskId (legacyId) a verdict refusal or a non-404 artefact
   * read failure belongs to; `undefined` for a `loadTaskActivity` failure
   * (Decision: `loadTaskActivity` has no per-task scope to carry). Rendered
   * inline by `BoardVerdictActions`/`ReviewCard` — never by `BuildQueue`,
   * which has its OWN unscoped channel, `listError` (fix wave 1,
   * GRP-B3D9F416, finding 3).
   */
  error: KeyedFeatureError | null
  /** The primary `openBoard` list-read failure ONLY — `BuildQueue`'s own error channel, so a scoped verdict/artefact refusal or an unscoped `loadTaskActivity` failure (both land in `error`) never unmounts the queue. */
  listError: KeyedFeatureError | null
  blocked: FeatureBlocked | null
  invalidationNonce: number
}

const initialState: BoardState = {
  projectId: null,
  tasks: [],
  backlog: [],
  epics: [],
  selectedTaskId: null,
  artefact: null,
  artefactLoading: false,
  artefactMissing: false,
  artefactError: null,
  activity: [],
  activityLoadedKey: null,
  activityLoading: false,
  activityInFlightKey: null,
  activityError: null,
  mutating: null,
  pulling: null,
  pullError: null,
  error: null,
  listError: null,
  blocked: null,
  invalidationNonce: 0,
  ...initialFetchCache
}

interface ProjectArg {
  orgId: string
  projectId: string
}

/** The status + code + message a primary-read rejection carries, for the blocked/error split. */
export interface StatusedError {
  status: number
  code?: string
  message: string
}

const toStatusedError = (error: unknown): StatusedError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, code: apiError.backendKey, message: apiError.message }
}

/** The same mapping, plus the row a refusal belongs to (`scope`). */
const toBoardError = (error: unknown, scope?: string): KeyedFeatureError => ({
  ...toStatusedError(error),
  scope
})

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * Mount: tasks + epics + backlog in one thunk (AC-1, revised by
 * CR-BUILD-BOARD-EPICS-2026-09-26). Guarded — one load per project. All three
 * reads share the project READ gate, so a 403 on any of them is the same
 * refusal and lands in `blocked` once. The payload widened additively; the
 * thunk's name, argument and condition are unchanged (INV-R.5).
 */
export const openBoard = createAsyncThunk<
  { tasks: BoardTaskRow[]; epics: BoardEpicRow[]; backlog: BoardTaskRow[] },
  ProjectArg,
  { rejectValue: StatusedError }
>(
  'board/open',
  async ({ orgId, projectId }, { rejectWithValue }) => {
    try {
      const [tasks, epics, backlog] = await Promise.all([
        boardService.listTasks(orgId, projectId),
        boardService.listEpics(orgId, projectId),
        boardService.listBacklog(orgId, projectId)
      ])
      return { tasks, epics, backlog }
    } catch (error) {
      return rejectWithValue(toStatusedError(error))
    }
  },
  {
    condition: ({ projectId }, { getState }) => {
      const { board } = getState() as { board: BoardState }
      return shouldFetch(board, projectId)
    },
    // A vetoed open is still an OPEN: it dispatches `rejected` with
    // `meta.condition`, and the reducer re-asserts the project on screen
    // (TASK-3D6DB8B6 fix cycle 3 — see the rejected case). No request is sent.
    dispatchConditionRejection: true
  }
)

/** Load one artefact's markdown for the selected task (Decision 5). */
export const loadArtefact = createAsyncThunk<
  { kind: ArtefactKindSuffix; markdown: string },
  ProjectArg & { taskId: string; kind: ArtefactKindSuffix },
  { rejectValue: StatusedError & { taskId: string } }
>('board/loadArtefact', async ({ orgId, projectId, taskId, kind }, { rejectWithValue }) => {
  try {
    const markdown = await boardService.getArtefactContent(orgId, projectId, taskId, kind)
    return { kind, markdown }
  } catch (error) {
    return rejectWithValue({ ...toStatusedError(error), taskId })
  }
})

/**
 * Load the project's task-scoped activity ONCE (Decision 1) — the run log
 * filters the cached rows to the selected task client-side.
 */
export const loadTaskActivity = createAsyncThunk<
  ActivityRow[],
  ProjectArg,
  { rejectValue: StatusedError }
>(
  'board/loadTaskActivity',
  async ({ orgId, projectId }, { rejectWithValue }) => {
    try {
      const response = await boardService.listTaskActivity(orgId, projectId)
      return response.items ?? []
    } catch (error) {
      return rejectWithValue(toStatusedError(error))
    }
  },
  {
    condition: ({ projectId }, { getState }) => {
      const { board } = getState() as { board: BoardState }
      // Keyed by project (fix cycle 1): only a read already in flight — or
      // already loaded — for THIS project suppresses the request.
      return board.activityInFlightKey !== projectId && board.activityLoadedKey !== projectId
    }
  }
)

/** Every verdict thunk swaps the RETURNED task in by `legacyId` on success. */
export const approveSpec = createAsyncThunk<
  BoardTaskRow,
  ProjectArg & { taskId: string },
  { rejectValue: KeyedFeatureError }
>('board/approveSpec', async ({ orgId, projectId, taskId }, { rejectWithValue }) => {
  try {
    return await boardService.approveSpec(orgId, projectId, taskId)
  } catch (error) {
    return rejectWithValue(toBoardError(error, taskId))
  }
})

export const requestSpecChanges = createAsyncThunk<
  BoardTaskRow,
  ProjectArg & { taskId: string; feedback: string },
  { rejectValue: KeyedFeatureError }
>('board/requestSpecChanges', async ({ orgId, projectId, taskId, feedback }, { rejectWithValue }) => {
  try {
    const input: FeedbackInput = { feedback }
    return await boardService.requestSpecChanges(orgId, projectId, taskId, input)
  } catch (error) {
    return rejectWithValue(toBoardError(error, taskId))
  }
})

export const approveTask = createAsyncThunk<
  BoardTaskRow,
  ProjectArg & { taskId: string },
  { rejectValue: KeyedFeatureError }
>('board/approveTask', async ({ orgId, projectId, taskId }, { rejectWithValue }) => {
  try {
    return await boardService.approve(orgId, projectId, taskId)
  } catch (error) {
    return rejectWithValue(toBoardError(error, taskId))
  }
})

export const requestChanges = createAsyncThunk<
  BoardTaskRow,
  ProjectArg & { taskId: string; feedback: string },
  { rejectValue: KeyedFeatureError }
>('board/requestChanges', async ({ orgId, projectId, taskId, feedback }, { rejectWithValue }) => {
  try {
    const input: FeedbackInput = { feedback }
    return await boardService.requestChanges(orgId, projectId, taskId, input)
  } catch (error) {
    return rejectWithValue(toBoardError(error, taskId))
  }
})

export const completeTesting = createAsyncThunk<
  BoardTaskRow,
  ProjectArg & { taskId: string },
  { rejectValue: KeyedFeatureError }
>('board/completeTesting', async ({ orgId, projectId, taskId }, { rejectWithValue }) => {
  try {
    return await boardService.completeTesting(orgId, projectId, taskId)
  } catch (error) {
    return rejectWithValue(toBoardError(error, taskId))
  }
})

export const rejectTesting = createAsyncThunk<
  BoardTaskRow,
  ProjectArg & { taskId: string; feedback: string },
  { rejectValue: KeyedFeatureError }
>('board/rejectTesting', async ({ orgId, projectId, taskId, feedback }, { rejectWithValue }) => {
  try {
    const input: FeedbackInput = { feedback }
    return await boardService.rejectTesting(orgId, projectId, taskId, input)
  } catch (error) {
    return rejectWithValue(toBoardError(error, taskId))
  }
})

/**
 * Pull one BACKLOG task onto the active board (`POST …/board/pull/:taskId`).
 * Not optimistic: the row moves only when the server answers it (see header).
 */
export const pullToBoard = createAsyncThunk<
  BoardTaskRow,
  ProjectArg & { taskId: string },
  { rejectValue: KeyedFeatureError }
>('board/pullToBoard', async ({ orgId, projectId, taskId }, { rejectWithValue }) => {
  try {
    return await boardService.pullFromBacklog(orgId, projectId, taskId)
  } catch (error) {
    return rejectWithValue(toBoardError(error, taskId))
  }
})

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

/** Replace (or append) one task row by `legacyId` — the swap-in every verdict shares. */
const swapTask = (state: BoardState, task: BoardTaskRow): void => {
  const index = state.tasks.findIndex((t) => t.legacyId === task.legacyId)
  if (index === -1) {
    state.tasks.push(task)
  } else {
    state.tasks[index] = task
  }
}

/** Shared pending/fulfilled/rejected wiring for the six verdict thunks. */
const addVerdictCases = <Arg extends { taskId: string }>(
  builder: ActionReducerMapBuilder<BoardState>,
  thunk: AsyncThunk<BoardTaskRow, Arg, { rejectValue: KeyedFeatureError }>
): void => {
  builder
    .addCase(thunk.pending, (state, action) => {
      state.mutating = action.meta.arg.taskId
      state.error = null
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state.mutating = null
      swapTask(state, action.payload)
    })
    .addCase(thunk.rejected, (state, action) => {
      state.mutating = null
      state.error = action.payload ?? null
    })
}

/**
 * Settle a `loadTaskActivity` request for `projectId` (fix cycle 1,
 * GRP-82297E9A). Clears the in-flight key only when it is THIS request's, and
 * returns whether the response still belongs to the current project — i.e.
 * the board shows `projectId`, or no board is open and this is the latest
 * request. A stale response (project A settling after the board moved to B)
 * returns `false` and must write nothing.
 */
function settleActivityRequest(state: BoardState, projectId: string): boolean {
  const isLatest = state.activityInFlightKey === projectId
  if (isLatest) {
    state.activityInFlightKey = null
    state.activityLoading = false
  }
  return state.projectId === projectId || (state.projectId === null && isLatest)
}

export const boardSlice = createSlice({
  name: 'board',
  initialState,
  reducers: {
    /**
     * Leave the workspace — resets everything, EXCEPT it never clears
     * `activityLoading` or `activityInFlightKey` (batch contract C-3).
     * StrictMode runs this cleanup BETWEEN a mounting panel's two effect runs;
     * clearing the in-flight key there would let `loadTaskActivity`'s guard
     * pass a second time and double-fire the request (deferred finding c1-8).
     * The settle reducers (`.fulfilled`/`.rejected`, via
     * `settleActivityRequest`) own both fields instead — the same rule
     * `fetchCache.invalidate` already follows for `loading`.
     */
    boardClosed(state) {
      state.projectId = null
      state.tasks = []
      state.backlog = []
      state.epics = []
      state.selectedTaskId = null
      state.artefact = null
      state.artefactLoading = false
      state.artefactMissing = false
      state.artefactError = null
      state.activity = []
      state.activityLoadedKey = null
      state.activityError = null
      state.mutating = null
      state.pulling = null
      state.pullError = null
      state.error = null
      state.listError = null
      state.blocked = null
      state.invalidationNonce = 0
      invalidate(state)
    },
    /** Dismiss a verdict refusal (the inline row error). */
    boardErrorDismissed(state) {
      state.error = null
    },
    /**
     * A Retry was requested after a failed board read: re-arm the cache so the
     * next `openBoard` is not vetoed by `shouldFetch` (a failed load leaves the
     * cache un-invalidated, so a bare retry was a silent no-op — memory
     * `px-retry-needs-an-explicit-invalidate`). `invalidate`, not
     * `markServerChanged`: nothing changed server-side.
     */
    boardRefetchArmed(state) {
      invalidate(state)
    },
    /** Select a task for the review card + run log. Clears the prior artefact. */
    taskSelected(state, action: { payload: string | null }) {
      state.selectedTaskId = action.payload
      state.artefact = null
      state.artefactMissing = false
      state.artefactError = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(openBoard.pending, (state, action) => {
        if (state.projectId !== action.meta.arg.projectId) {
          state.tasks = []
          state.backlog = []
          state.epics = []
          state.selectedTaskId = null
          state.blocked = null
        }
        fetchStarted(state, action.meta.arg.projectId)
        state.projectId = action.meta.arg.projectId
        state.error = null
        state.listError = null
      })
      .addCase(openBoard.fulfilled, (state, action) => {
        // Key-scoped (TASK-3D6DB8B6): a superseded project's reply writes nothing.
        if (!fetchSucceeded(state, action.meta.arg.projectId)) return
        state.tasks = action.payload.tasks ?? []
        state.epics = action.payload.epics ?? []
        state.backlog = action.payload.backlog ?? []
        state.blocked = null
      })
      .addCase(openBoard.rejected, (state, action) => {
        if (action.meta.condition) {
          // A VETOED open (TASK-3D6DB8B6 fix cycle 3, the projectDetail
          // precedent): this project's load is in flight or its rows are
          // fresh, so the store holds (or is about to hold) THIS project.
          // `boardClosed` — run by StrictMode between the effect runs, or by
          // leaving the Build page mid-load — nulled `projectId`; without the
          // re-assert the follow-up's on-screen check and
          // `BoardVerdictActions` on /my/board went dead. The cache is
          // untouched, and a genuine close is never undone: only an explicit
          // open reaches this branch.
          state.projectId = action.meta.arg.projectId
          return
        }
        if (!fetchFailed(state, action.meta.arg.projectId)) return
        const rejection = action.payload
        // 403/404 -> blocked, everything else -> listError; exactly one
        // non-null (INV-4.5). `listError` (not `error`, finding 3) is
        // `BuildQueue`'s OWN channel — the primary list read's failure,
        // never a scoped verdict/artefact refusal or an unscoped
        // `loadTaskActivity` failure, both of which still land in `error`.
        if (rejection !== undefined && (rejection.status === 403 || rejection.status === 404)) {
          state.blocked = { status: rejection.status, code: rejection.code, message: rejection.message }
          state.listError = null
        } else {
          state.blocked = null
          state.listError =
            rejection === undefined
              ? null
              : { code: rejection.code, message: rejection.message, status: rejection.status }
        }
      })
      .addCase(loadArtefact.pending, (state) => {
        state.artefactLoading = true
        state.artefactMissing = false
        state.artefactError = null
      })
      .addCase(loadArtefact.fulfilled, (state, action) => {
        state.artefactLoading = false
        state.artefactMissing = false
        state.artefactError = null
        state.artefact = action.payload
      })
      .addCase(loadArtefact.rejected, (state, action) => {
        state.artefactLoading = false
        const rejection = action.payload
        if (rejection !== undefined && rejection.status === 404) {
          // Decision 5 / INV-4.4: a missing artefact is the review card's own
          // empty state, never the page's blocked state.
          state.artefactMissing = true
          state.artefact = null
        } else if (rejection !== undefined) {
          // Deferred findings c1-4/c2-2: a non-404 artefact failure is its OWN
          // channel (`artefactError`), never the shared `error` — the pane
          // must be able to tell "loading" and "failed" apart, which a
          // channel shared with unrelated verdict refusals cannot do.
          state.artefactError = {
            code: rejection.code,
            message: rejection.message,
            status: rejection.status
          }
        }
      })
      .addCase(loadTaskActivity.pending, (state, action) => {
        state.activityInFlightKey = action.meta.arg.projectId
        state.activityLoading = true
        state.activityError = null
      })
      .addCase(loadTaskActivity.fulfilled, (state, action) => {
        if (!settleActivityRequest(state, action.meta.arg.projectId)) return
        state.activity = action.payload
        state.activityLoadedKey = action.meta.arg.projectId
      })
      .addCase(loadTaskActivity.rejected, (state, action) => {
        if (!settleActivityRequest(state, action.meta.arg.projectId)) return
        const rejection = action.payload
        if (rejection !== undefined) {
          // Deferred finding c2-1: its OWN channel (`activityError`), never
          // the shared `error` — RunLogPanel had no error branch at all, so a
          // dead GET used to read as "this task has no activity" (a false
          // empty state indistinguishable from a genuinely quiet task).
          state.activityError = { code: rejection.code, message: rejection.message, status: rejection.status }
        }
      })
      .addCase(invalidateBoard, (state) => {
        // Realtime canon: refetch-on-invalidate, no delta-merge (Decision 7).
        // No new EVENT_TYPE_MAP row and no new nonce family — the four
        // `task.*` rows already map to this EXISTING action. A server change:
        // mid-flight it survives the settle and earns one follow-up load.
        state.invalidationNonce += 1
        state.activityLoadedKey = null
        markServerChanged(state)
      })
    builder
      .addCase(pullToBoard.pending, (state, action) => {
        state.pulling = action.meta.arg.taskId
        state.pullError = null
      })
      .addCase(pullToBoard.fulfilled, (state, action) => {
        if (state.pulling === action.meta.arg.taskId) state.pulling = null
        // A reply for a board no longer on screen writes nothing.
        if (state.projectId !== action.meta.arg.projectId) return
        state.backlog = state.backlog.filter((task) => task.legacyId !== action.meta.arg.taskId)
        swapTask(state, action.payload)
      })
      .addCase(pullToBoard.rejected, (state, action) => {
        if (state.pulling === action.meta.arg.taskId) state.pulling = null
        if (state.projectId !== action.meta.arg.projectId) return
        state.pullError = action.payload ?? {
          scope: action.meta.arg.taskId,
          message: action.error.message ?? '',
          status: 0
        }
      })
    addVerdictCases(builder, approveSpec)
    addVerdictCases(builder, requestSpecChanges)
    addVerdictCases(builder, approveTask)
    addVerdictCases(builder, requestChanges)
    addVerdictCases(builder, completeTesting)
    addVerdictCases(builder, rejectTesting)
  }
})

export const { boardClosed, boardErrorDismissed, boardRefetchArmed, taskSelected } = boardSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithBoard {
  board: BoardState
}

/**
 * The project `openBoard` last loaded for — recorded regardless of whether
 * the caller reached it through a route param (`/project/:projectId/build`)
 * or a prop (`/my/board`, `/my/project`, which carry no `:projectId`
 * segment). Fix wave 1 (GRP-B3D9F416, TASK-41CE1DE2), finding 2:
 * `ReviewCard`/`RunLogPanel`/`BoardVerdictActions` read THIS first, falling
 * back to `useParams().projectId` only when it is `null` — on `/my/*` the
 * route param is always `undefined`, so reading it alone left those three
 * panels permanently inert there.
 */
export const selectBoardProjectId = (s: WithBoard): string | null => s.board.projectId
export const selectBoardTasks = (s: WithBoard): BoardTaskRow[] => s.board.tasks
export const selectBoardEpics = (s: WithBoard): BoardEpicRow[] => s.board.epics
export const selectBoardLoading = (s: WithBoard): boolean => s.board.loading
export const selectBoardError = (s: WithBoard): KeyedFeatureError | null => s.board.error
/** `BuildQueue`'s own error channel — the primary list read's failure only (finding 3). */
export const selectBoardListError = (s: WithBoard): KeyedFeatureError | null => s.board.listError
export const selectBoardBlocked = (s: WithBoard): FeatureBlocked | null => s.board.blocked
/**
 * "Did the board's primary read fail?" in one call (deferred finding c3-2) —
 * `listError` and `blocked` are mutually exclusive (INV-4.5), so a consumer
 * that only ever checked one of the two silently missed the other.
 */
export const selectBoardReadFailed = (s: WithBoard): KeyedFeatureError | FeatureBlocked | null =>
  s.board.listError ?? s.board.blocked
export const selectBoardNonce = (s: WithBoard): number => s.board.invalidationNonce
export const selectSelectedTaskId = (s: WithBoard): string | null => s.board.selectedTaskId

/** Shared empty result, so an empty board never hands a selector consumer a fresh array. */
const NO_TASKS: BoardTaskRow[] = []

/** The BACKLOG pool `openBoard` loaded beside the active board (CR-BUILD-BOARD-EPICS-2026-09-26). */
export const selectBoardBacklog = (s: WithBoard): BoardTaskRow[] => s.board.backlog

/**
 * Active board + backlog, in load order (tasks first, then backlog rows —
 * spec R.6.2: no client sort is invented). The Build board's two views and
 * the /my boards project THIS list; `selectBoardTasks` stays active-only for
 * the overview (INV-R.5).
 *
 * De-duplicated by `legacyId`, the board row winning (fix cycle 1, review
 * low): `openBoard` reads tasks and backlog with two concurrent GETs, so a
 * pull or push landing between them can return one task in BOTH lists, and
 * the card would show twice until the next reload.
 */
export const selectBoardAllTasks = createSelector(
  [selectBoardTasks, selectBoardBacklog],
  (tasks, backlog): BoardTaskRow[] => {
    if (tasks.length + backlog.length === 0) return NO_TASKS
    if (backlog.length === 0) return tasks
    const onBoard = new Set(tasks.map((task) => task.legacyId))
    const backlogOnly = backlog.filter((task) => !onBoard.has(task.legacyId))
    return backlogOnly.length === 0 ? tasks : [...tasks, ...backlogOnly]
  }
)

/**
 * Whether the review panels (artefact card, verdicts, run log) have anything
 * to serve (fix cycle 1, review low): the board's read did not fail and it
 * holds at least one task. False while the first load is in flight, on the
 * empty board and on a blocked or failed read — the panels render under none
 * of those. A reload keeps them, since the rows stay loaded while it runs.
 */
export const selectBoardHasTasks = createSelector(
  [selectBoardAllTasks, selectBoardReadFailed],
  (all, readFailed): boolean => readFailed === null && all.length > 0
)

/** The selected card's row — a backlog card is selectable too, so it reads the combined list. */
export const selectSelectedTask = createSelector(
  [selectBoardAllTasks, selectSelectedTaskId],
  (tasks, selectedTaskId) => tasks.find((t) => t.legacyId === selectedTaskId) ?? null
)
export const selectArtefactContent = (s: WithBoard) => s.board.artefact
export const selectArtefactLoading = (s: WithBoard): boolean => s.board.artefactLoading
export const selectArtefactMissing = (s: WithBoard): boolean => s.board.artefactMissing
/** A non-404 `loadArtefact` failure (deferred findings c1-4/c2-2) — `ReviewCard`'s own channel. */
export const selectArtefactError = (s: WithBoard): StatusedError | null => s.board.artefactError
export const selectMutatingTaskId = (s: WithBoard): string | null => s.board.mutating
/** The backlog task whose pull is in flight. */
export const selectPullingTaskId = (s: WithBoard): string | null => s.board.pulling
/** A refused pull, scoped to one task's `legacyId` (the card renders only its own). */
export const selectPullError = (s: WithBoard): KeyedFeatureError | null => s.board.pullError

type WithBoardAndDetail = WithBoard & Parameters<typeof selectCanWriteProject>[0]

/**
 * Whether Pull to board renders for `projectId`'s cards — fails closed: the
 * project detail on screen must be THIS board's project and grant `write` (the
 * server's own gate is project WRITE). /my boards open no project detail, so
 * the action is absent there.
 */
export const selectCanPullToBoard = (s: WithBoardAndDetail, projectId: string): boolean =>
  s.board.projectId === projectId && s.projectDetail.projectId === projectId && selectCanWriteProject(s)
export const selectActivityLoading = (s: WithBoard): boolean => s.board.activityLoading
/** A `loadTaskActivity` failure (deferred finding c2-1) — `RunLogPanel`'s own channel. */
export const selectActivityError = (s: WithBoard): StatusedError | null => s.board.activityError

/** Filtered to ONE task's rows, in server order (the project feed is fetched once). */
export const selectTaskActivity = createSelector(
  [(s: WithBoard) => s.board.activity, (_s: WithBoard, taskId: string | null) => taskId],
  (activity, taskId) =>
    taskId === null
      ? []
      : activity.filter((row) =>
          (row.contextIds ?? []).some((ref) => ref.type === 'taskId' && ref.id === taskId)
        )
)

/**
 * Aggregate progress over the loaded tasks. `byStatus` carries every wire
 * value (0 where absent) — a `Record<TaskStatusValue, number>` — and
 * CANCELLED is excluded from both `total`'s numerator/denominator and the
 * ratio, per `TaskStatus.countsTowardTotal`.
 */
export const selectBoardProgress: (s: WithBoard) => BoardProgress = createSelector(
  [selectBoardTasks],
  (tasks): BoardProgress => {
    const byStatus = TASK_STATUS_VALUES.reduce<Record<TaskStatusValue, number>>(
      (acc, status) => ({ ...acc, [status]: 0 }),
      {} as Record<TaskStatusValue, number>
    )
    for (const task of tasks) {
      byStatus[task.status] = (byStatus[task.status] ?? 0) + 1
    }
    const counted = tasks.filter((task) => task.status !== EXCLUDED_FROM_TOTAL)
    const total = counted.length
    const done = counted.filter((task) => PROGRESS_WEIGHTS[task.status] === 1).length
    const weightSum = counted.reduce((sum, task) => sum + (PROGRESS_WEIGHTS[task.status] ?? 0), 0)
    const ratio = total === 0 ? 0 : weightSum / total
    return { total, done, ratio, byStatus }
  }
)

/** Tasks whose next move is a named human's — derived, never stored. */
export const selectTasksAwaitingHuman: (s: WithBoard) => BoardTaskRow[] = createSelector(
  [selectBoardTasks],
  (tasks) => tasks.filter((task) => (HUMAN_GATED_STATUSES as readonly string[]).includes(task.status))
)

/**
 * "My work" = assignee OR reviewer, matched against the SAME principal key
 * every other "is this mine" comparison in the app uses (Decision 2, INV-4.7).
 * A DISPLAY filter, not an authorization decision — the server already
 * decided what the caller may read.
 */
export const selectMyTasks = createSelector(
  [
    selectBoardAllTasks,
    (s: WithBoardAndSession) => selectSessionPrincipalKey(s)
  ],
  (tasks, key): BoardTaskRow[] =>
    key === null ? NO_TASKS : tasks.filter((task) => task.assigneeId === key || task.reviewerId === key)
)

type WithBoardAndSession = WithBoard & Parameters<typeof selectSessionPrincipalKey>[0]

/** The arguments of a {@link makeSelectBoardColumns} selector — passed per call, compared as primitives. */
export interface BoardColumnsArgs {
  narrowToCaller: boolean
  epicFilter: EpicFilter
}

const filterOf = (kind: EpicFilter['kind'], id: string | null): EpicFilter =>
  kind === 'epic' && id !== null ? { kind: 'epic', id } : kind === 'unassigned' ? { kind: 'unassigned' } : { kind: 'all' }

/** Narrow rows to one epic filter; `unassigned` = no epicId, or an epicId naming no loaded epic. */
const narrowByEpic = (
  rows: BoardTaskRow[],
  filter: EpicFilter,
  epicIds: ReadonlySet<string>
): BoardTaskRow[] => {
  if (filter.kind === 'all') return rows
  if (filter.kind === 'unassigned') {
    return rows.filter((task) => task.epicId === undefined || !epicIds.has(task.epicId))
  }
  return rows.filter((task) => task.epicId === filter.id)
}

/**
 * Selector FACTORY for the Tasks view's four columns (spec R.6.2, INV-R.1):
 * every row lands in exactly the column `BOARD_COLUMN_OF` names, in load
 * order; an off-board or unknown status lands in none. Each component
 * instance creates its own (`useMemo(makeSelectBoardColumns, [])`) so two
 * mounted boards never evict each other's cache; the arguments are read as
 * primitives, so a fresh `args` object per render still hits the cache.
 */
export const makeSelectBoardColumns = () =>
  createSelector(
    [
      selectBoardAllTasks,
      selectMyTasks,
      selectBoardEpics,
      (_s: WithBoardAndSession, args: BoardColumnsArgs) => args.narrowToCaller,
      (_s: WithBoardAndSession, args: BoardColumnsArgs) => args.epicFilter.kind,
      (_s: WithBoardAndSession, args: BoardColumnsArgs) =>
        args.epicFilter.kind === 'epic' ? args.epicFilter.id : null
    ],
    (all, mine, epics, narrowToCaller, kind, id): BoardColumns => {
      const epicIds = new Set(epics.map((epic) => epic.legacyId))
      const rows = narrowByEpic(narrowToCaller ? mine : all, filterOf(kind, id), epicIds)
      const columns: BoardColumns = { backlog: [], buildQueue: [], review: [], done: [] }
      for (const task of rows) {
        const column = BOARD_COLUMN_OF[task.status]
        if (column) columns[column].push(task)
      }
      return columns
    }
  )

interface EpicCounts {
  total: number
  backlog: number
  buildQueue: number
  awaitingReview: number
  done: number
}

const emptyCounts = (): EpicCounts => ({ total: 0, backlog: 0, buildQueue: 0, awaitingReview: 0, done: 0 })

const summaryOf = (key: string, title: string | null, counts: EpicCounts): EpicSummary => {
  const ratio = counts.total === 0 ? 0 : counts.done / counts.total
  return { key, title, ...counts, ratio, percent: Math.round(ratio * 100) }
}

const NO_SUMMARIES: EpicSummary[] = []

/**
 * The Epics view's cards (spec R.6.4, INV-R.2), one pass over active +
 * backlog rows: each row with a non-null column counts on exactly one card —
 * its epic's when the epicId names a loaded epic, else the Unassigned card.
 * Cards follow `GET …/epics` order (a 0/0 epic still gets its card); the
 * Unassigned card comes last and only when it counts something. The server's
 * `progress` / `completedTickets` are never read: they count TESTING, UAT and
 * ARCHIVED as complete.
 */
export const selectEpicSummaries = createSelector(
  [selectBoardAllTasks, selectBoardEpics],
  (all, epics): EpicSummary[] => {
    if (all.length === 0 && epics.length === 0) return NO_SUMMARIES
    const byEpic = new Map<string, EpicCounts>(epics.map((epic) => [epic.legacyId, emptyCounts()]))
    const unassigned = emptyCounts()
    for (const task of all) {
      const column = BOARD_COLUMN_OF[task.status]
      if (!column) continue
      const counts = (task.epicId !== undefined ? byEpic.get(task.epicId) : undefined) ?? unassigned
      counts.total += 1
      if (column === 'backlog') counts.backlog += 1
      else if (column === 'buildQueue') counts.buildQueue += 1
      else if (column === 'review') counts.awaitingReview += 1
      else counts.done += 1
    }
    const summaries = epics.map((epic) =>
      summaryOf(epic.legacyId, epic.title ?? null, byEpic.get(epic.legacyId) ?? emptyCounts())
    )
    if (unassigned.total > 0) summaries.push(summaryOf(UNASSIGNED_EPIC_KEY, null, unassigned))
    return summaries
  }
)

/** Epic legacyId → display title (an untitled epic reads as its legacyId), memoised over the loaded epics. */
const selectEpicTitleIndex = createSelector([selectBoardEpics], (epics): ReadonlyMap<string, string> => {
  const index = new Map<string, string>()
  for (const epic of epics) {
    index.set(epic.legacyId, epic.title !== undefined && epic.title !== '' ? epic.title : epic.legacyId)
  }
  return index
})

/** One epic's display title, or `null` when no loaded epic carries that legacyId. */
export const selectEpicTitle = createSelector(
  [selectEpicTitleIndex, (_s: WithBoard, epicId: string | undefined) => epicId],
  (index, epicId): string | null => (epicId === undefined ? null : (index.get(epicId) ?? null))
)
